import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useLocalStorage } from '../../../hooks/useLocalStorage'
import { getCurrentDateTime, guessUserTZ } from '../../../lib/time'
import { useGetEntityFormByType } from '../../../hooks/entityFormHook'
import {
  useGetTimeMaterialReportFormData,
  useGetTimeMaterialReportFormTimeSheets,
  useMutateCreateTimeMaterialReport,
} from './hooks'
import { Alert, Box, Container } from '@mui/material'
import { BackButton } from '../../../components/navigation/BackButton'
import { Form } from '../../../components/Form'
import { ListedTimeSheets } from '../../../components/timesheet/ListedTimesheets'
import { Fetching } from '../../../components/Fetching'
import { debounce } from 'lodash'
import SnackBarContext from '../../../context/SnackBarContext'

export const TimeMaterialFormNew = () => {
  const defaultReportState: any = { date: getCurrentDateTime() }
  const { id, tmId, tmrId } = useParams()
  const navigate = useNavigate()
  const [reportDate, setReportDate] = useState(getCurrentDateTime())
  const [timeInMaterialReport, setTimeInMaterialReport] =
    useState(defaultReportState)
  const { data: form, isLoading: isFormLoading } = useGetEntityFormByType(
    'timeMaterialReportForm',
    'timeAndMaterialReport'
  )
  const { data: formData, isLoading: isFormDataLoading } =
    useGetTimeMaterialReportFormData('timematerial', id, tmId, tmrId)
  const { data: timeSheetsData, isLoading: isTimeSheetsDataLoading } =
    useGetTimeMaterialReportFormTimeSheets('timematerial', id, tmId, reportDate)
  const { mutateAsync: createTMReport } = useMutateCreateTimeMaterialReport(
    'timematerialreport',
    id,
    tmId
  )
  const { toggleSnackbar } = useContext(SnackBarContext);
  const [errorMessage, setErrorMessage] = useState<any | undefined>('')

  const onChangeHandler = (newFormData: any) => {
    setReportDate(newFormData.date)
    setTimeInMaterialReport((prevReportData: any) => ({
      ...prevReportData,
      ...newFormData,
    }))
  }

  const onSubmitHandler = useCallback(debounce(async timeInMaterial => {
    let hasEmptyValues = form.fields.some((f: any) => {
      const value = timeInMaterialReport[f.name]
      return typeof value === 'string' && value.trim() === '' && f.isRequired
    })

    if (hasEmptyValues) {
      setErrorMessage('Please enter valid values.')
      return
    }

    setErrorMessage('')
    // Removed timeSheetsData from parameters to use the latest state directly
    const timeMaterialReport = {
      ...timeInMaterial,
      date: reportDate,
      timecards: timeSheetsData, // Now directly using the latest state
      finalized: true,
    }

    try {
      const result = await createTMReport(timeMaterialReport)
      navigate(
        `/projects/${timeInMaterial.project}/time-material/${tmId}/report/${result.report._id}`
      )
      toggleSnackbar('Time Material report created successfully', 'success')
    } catch (error) {
      console.error('Error creating report:', error)
      setErrorMessage('Failed to create report.')
    }
  }, 500),
    [reportDate, timeSheetsData, timeInMaterialReport] // Ensure timeSheetsData is included in dependency array
  )

  useEffect(() => {
    if (formData) {
      setTimeInMaterialReport((prevReportData: any) => ({
        ...prevReportData,
        ...formData,
        date: formData.date ? formData.date : reportDate,
      }))
    }
  }, [formData, reportDate])

  if (isFormLoading || isFormDataLoading || isTimeSheetsDataLoading) {
    return <Fetching />
  }

  return (
    <>
      <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
        <Container maxWidth='lg'>
          <BackButton />
        </Container>
      </Box>
      {form && (
        <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
          <Container maxWidth='lg'>
            {errorMessage && <Alert severity='error' sx={{ marginBottom: 2 }}>{errorMessage}</Alert>}
            {timeInMaterialReport && (
              <Form
                fields={form.fields}
                onSubmit={() => onSubmitHandler(timeInMaterialReport)} // Adjusted to call without timeSheetsData parameter
                record={timeInMaterialReport}
                onFormDataChange={onChangeHandler}
                submitLabel={'Create'}>
                {!isTimeSheetsDataLoading && (
                  <ListedTimeSheets timeSheets={timeSheetsData} />
                )}
              </Form>
            )}
          </Container>
        </Box>
      )}
    </>
  )
}
